import log from "loglevel"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { API, NexusSuccessResponse } from "../api/API"
import {
    AcquisitionMode,
    AcquisitionModeInFlightplan,
    AcquisitionModesForType,
    FlightplanSensorConfiguration,
} from "../models/AcquisitionMode"
import { FlightPlannedLineType, FlightplanStatus, FlightplanType } from "../store/types/FlightPlansTypes"
import { insertTemporaryTableId } from "../utils/TableUtils"
import { groupAcquisitionModes } from "./AcquisitionModeStore"

interface FlightPlannedLineCreateDto extends Omit<FlightPlannedLineType, "planned_line_id"> {
    planned_line_id?: number
}

interface FlightplanEditState {
    fetchError?: string
    parsingFile: boolean
    fetchingFlightplans: boolean
    selectingFlightplan: boolean
    flightplansForProject: FlightplanType[]
    selectedFlightplan: FlightplanType
    selectedFlightplanPlannedLines: FlightPlannedLineType[]
    deletedPlannedLineIds: number[]
    warnings: string[]

    // Mode we are viewing right now
    viewAcqMode?: {
        mode: AcquisitionMode
        configs: FlightplanSensorConfiguration[]
    }
    modesByType: AcquisitionModesForType[]
    selectedModes: AcquisitionModeInFlightplan[]
}

interface FlightplanEditActions {
    fetchFlightplansForProject: (projectNumber: string) => Promise<FlightplanType[]>
    selectFlightplanForProject: (flightplan: FlightplanType) => Promise<void>
    updatePlannedLines: (activeLines: FlightPlannedLineType[], deleted: number[]) => Promise<void>
    parseFlightplanFile: (projectNumber: string, fileName: string, file: File) => Promise<void>

    setViewAcqMode: (mode: AcquisitionMode) => Promise<void>
    setAvailableModes: (modes: AcquisitionMode[]) => Promise<void>
    updateConfigForMode: (
        modeId: number,
        sensorConfigIdx: number,
        config: Partial<FlightplanSensorConfiguration>,
    ) => Promise<void>
    addConfigForMode: (modeId: number) => Promise<void>
    removeConfigForMode: (modeId: number) => Promise<void>
    toggleSelectedMode: (modeId: number) => Promise<void>
    initializeModes: (modes: AcquisitionModeInFlightplan[]) => Promise<void>

    updateFlightplanField: <FieldType extends keyof FlightplanType>(
        field: FieldType,
        value: FlightplanType[FieldType],
    ) => void

    clearError: () => Promise<void>
    reset: () => void
    resetAcquisitionModes: () => Promise<void>
}

const initialState: Readonly<FlightplanEditState> = {
    parsingFile: false,
    fetchingFlightplans: false,
    selectingFlightplan: false,
    flightplansForProject: [],
    selectedFlightplanPlannedLines: [],
    modesByType: [],
    selectedModes: [],
    deletedPlannedLineIds: [],
    warnings: [],

    // Blank for create flow
    selectedFlightplan: {},
}

const createNewSensorConfig = (): FlightplanSensorConfiguration => ({
    name: "",
})

/**
 * This zustand store helps share data between the component creating the state, FlightplanAcquisitionModeSelect.tsx
 * And the API call that pushes the data to the server.
 */
export const useFlightplanEditStore = create<FlightplanEditState & FlightplanEditActions>()(
    immer((set, get) => ({
        ...initialState,
        updatePlannedLines: async (active, deletedIds) =>
            set((state) => {
                state.selectedFlightplanPlannedLines = active
                state.deletedPlannedLineIds.push(...deletedIds)
            }),
        updateFlightplanField: async (field, value) =>
            set((state) => {
                state.selectedFlightplan[field] = value
            }),
        fetchFlightplansForProject: async (projectNumber: string) => {
            set({ fetchingFlightplans: true })
            try {
                const result: NexusSuccessResponse<FlightplanType[]> = await API.get(
                    `/flightplans/${projectNumber}`,
                    `Couldn't fetch flightplans for project number: ${projectNumber}`,
                )

                set({ flightplansForProject: result.data, fetchingFlightplans: false })
                return result.data
            } catch (e) {
                log.error("Error fetching flightplans for project", e)
                set({ fetchError: e.message, fetchingFlightplans: false })
                return []
            }
        },
        selectFlightplanForProject: async (selectedFlightplan: FlightplanType) => {
            set({ selectingFlightplan: true })
            try {
                const result: NexusSuccessResponse<FlightPlannedLineType[]> = await API.get(
                    `/flight-planned-lines/${selectedFlightplan.flight_plan_id}`,
                    `Error in retrieving flight planned lines in Flightplan with flightplan ID: ${selectedFlightplan.flight_plan_id}.`,
                )

                set({ selectedFlightplanPlannedLines: result.data, selectedFlightplan, selectingFlightplan: false })
            } catch (e) {
                log.error("Error fetching flightplans for project", e)
                set({ fetchError: e.message, selectingFlightplan: false })
            }
        },
        initializeModes: async (modes: AcquisitionModeInFlightplan[]) => set({ selectedModes: modes }),
        addConfigForMode: async (modeId: number) => {
            set((state) => {
                state.viewAcqMode?.configs?.push(createNewSensorConfig())
                state.selectedModes
                    .find((selectedMode) => selectedMode.mode_id === modeId)
                    ?.sensor_configurations.push(createNewSensorConfig())
            })
        },
        removeConfigForMode: async (removeConfigIdx: number) => {
            set((state) => {
                const currentModeId = state.viewAcqMode?.mode?.mode_id
                if (currentModeId == null) {
                    throw new Error("No current mode, cannot remove config!")
                }
                state.viewAcqMode?.configs?.splice(removeConfigIdx, 1)
                state.selectedModes
                    .find((selectedMode) => selectedMode.mode_id === currentModeId)
                    ?.sensor_configurations.splice(removeConfigIdx, 1)
            })
        },
        updateConfigForMode: async (
            modeId: number,
            sensorConfigIdx: number,
            sensorConfig: Partial<FlightplanSensorConfiguration>,
        ) => {
            set((state) => {
                const updateModeIdx = state.selectedModes.findIndex((mode) => mode.mode_id === modeId)
                const existingConfig = state.selectedModes[updateModeIdx]?.sensor_configurations[sensorConfigIdx]
                if (existingConfig == null) {
                    throw new Error(
                        `Couldn't find existing config for mode with id ${modeId} and sensor config idx ${sensorConfigIdx}`,
                    )
                }
                if (state.viewAcqMode != null) {
                    // Update the sensor config we are currently editing
                    const existingConfig = state.viewAcqMode.configs[sensorConfigIdx] ?? { name: "" }
                    state.viewAcqMode.configs[sensorConfigIdx] = {
                        ...existingConfig,
                        ...sensorConfig,
                    }
                }
                state.selectedModes[updateModeIdx].sensor_configurations[sensorConfigIdx] = {
                    ...existingConfig,
                    ...sensorConfig,
                }
            })
        },
        setAvailableModes: async (modes: AcquisitionMode[]) => {
            const state = get()
            const modesByType = await groupAcquisitionModes(modes)
            set({ modesByType })
            await state.setViewAcqMode(modesByType[0].modes[0])
        },
        toggleSelectedMode: async (modeId: number) => {
            set((state) => {
                const indexOfMode = state.selectedModes.findIndex((mode) => mode.mode_id === modeId)
                if (indexOfMode === -1) {
                    // Add a new mode
                    state.selectedModes.push({
                        mode_id: modeId,
                        sensor_configurations: [{ name: "" }],
                    })
                } else {
                    // Remove existing mode
                    state.selectedModes.splice(indexOfMode, 1)
                }
            })
        },
        setViewAcqMode: async (mode: AcquisitionMode) => {
            const state = get()

            const modeDetails = state.selectedModes.find((selectedMode) => selectedMode.mode_id === mode.mode_id)

            if (modeDetails != null) {
                // Mode is selected. Initialize the configs to its sensor configs
                set({
                    viewAcqMode: {
                        mode,
                        configs: modeDetails?.sensor_configurations ?? [createNewSensorConfig()],
                    },
                })
            } else {
                // Find the mode among non-selected ones.
                const modeToSelect: AcquisitionMode | undefined = state.modesByType
                    .flatMap((modeType) => modeType.modes)
                    .find((availableMode) => availableMode.mode_id === mode.mode_id)
                if (modeToSelect == null) {
                    throw new Error(`No acquisition mode exists with id ${mode.mode_id}`)
                }
                set({
                    viewAcqMode: {
                        mode: modeToSelect,
                        configs: [createNewSensorConfig()],
                    },
                })
            }
        },
        parseFlightplanFile: async (projectNumber, fileName, file) => {
            set({ parsingFile: true })
            const parserError = true

            try {
                const formData = new FormData()
                formData.append("file", file)

                const response: {
                    data: {
                        flight_planned_lines: FlightPlannedLineCreateDto[]
                        warnings: string[]
                        success: boolean
                        source_file: string | null
                    }
                } = await API.uploadFile("/upload/flightplan", formData)

                const flightplan: FlightplanType = {
                    flight_plan_name: fileName,
                    project_number: projectNumber,
                    status: FlightplanStatus.Active,
                    source_file: response.data.source_file ?? undefined,
                }
                let flightPlannedLines: FlightPlannedLineType[] = response.data.flight_planned_lines.map(
                    (plannedLine, idx) => {
                        const newPlannedLine: FlightPlannedLineType = {
                            ...plannedLine,
                            planned_line_id: insertTemporaryTableId(idx),
                        }

                        if (plannedLine.planned_nodes) {
                            newPlannedLine.node_count = plannedLine.planned_nodes.length
                        }

                        return newPlannedLine
                    },
                )
                flightPlannedLines = flightPlannedLines.sort((a, b) =>
                    a.flight_line_name > b.flight_line_name ? 1 : -1,
                )

                set({
                    selectedFlightplan: flightplan,
                    selectedFlightplanPlannedLines: flightPlannedLines,
                    warnings: response.data.warnings,
                    parsingFile: false,
                })
            } catch (error) {
                log.error("Error parsing flightplan file.", error)
                let errorMessage = "Error uploading file."
                if (parserError) {
                    errorMessage = error.message
                }
                if (error.message === "413") {
                    errorMessage = "File too large (50MB max)."
                }

                set({ fetchError: errorMessage, parsingFile: false })
            }
        },
        clearError: async () => set({ fetchError: undefined }),
        reset: () => set(initialState),
        resetAcquisitionModes: async () => set({ modesByType: [], selectedModes: [], viewAcqMode: undefined }),
    })),
)
